import {
  CreateNewCollectibleData,
  CollectibleType,
  UnsavedCollectible,
} from "@/types/highlights"
import { utils } from "ethers"

import * as Sentry from "@sentry/nextjs"

import { createPendingHighlight } from "api_routes/highlights"
import Button from "components/Button"
import { selectBlog } from "features/blogSlice"
import { selectCurrentNote } from "features/noteSlice"
import { retrieveHighlights } from "features/pageSlice"
import useCreateAndCollectHighlight from "hooks/crypto/highlights/useCreateAndCollectHighlight"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { useAppDispatch } from "store"
import { useAnalytics } from "hooks/useAnalytics"
import SwitchChainButton from "components/SwitchChainButton"
import ErrorMessage from "components/Collectibles/ErrorMessage"

export default function CreateNewCollectibleBtn(
  props: CreateNewCollectibleData & {
    type: CollectibleType
    text: string
    btnText?: string
  }
) {
  const blog = useSelector(selectBlog)
  const note = useSelector(selectCurrentNote)
  const dispatch = useAppDispatch()
  const { data, buy, error } = useCreateAndCollectHighlight(props)
  const { track } = useAnalytics()

  console.log("Collecting highlight with props:", props)

  // Creating the highlight after the user successfully purchases it but before
  // the block is cleared.
  useEffect(() => {
    if (!data?.hash) {
      console.log("No hash present yet, so doing nothing.")
      return
    }

    if (!note?.id) {
      console.log("No note, so doing nothing.")
      track("no note when creating collectible")
      Sentry.captureMessage("No note when creating collectible.")
      return
    }

    if (!props.text) {
      Sentry.captureMessage("No text selected when creating collectible.")
      track("no text when creating collectible")
      console.error("No text selected when creating collectible.")
      return
    }

    const pendingCollectible: UnsavedCollectible = {
      blogId: blog.id,
      noteId: note?.id,
      createdAt: Date.now(),
      type: props.type,
      status: "PENDING",
      // We set this here but it's overridden on the backend.
      version: 1,
      text: props.text,
      position: props.position,
      txHash: data.hash.toLowerCase(),
      collectorWallet: props.minterAddress?.toLowerCase(),
      costEth: utils.formatEther(props.costWei.toString()),
      supply: props.supply.toString(),
      chain: props.chain,
    }

    console.trace("Creating pending highlight:", pendingCollectible)

    // Create a pending collectible.
    // This blocks everyone else frm creating the same collectible.
    createPendingHighlight(pendingCollectible).then((res) => {
      console.log("Created highlight!")
      if (res.success) {
        track("pending collectible created successfully", {
          pendingCollectible,
        })

        // Refresh collectibles on the page.
        // This will show the current one as "PENDING".
        // When the tx clears, useUpdateHighlightState will update
        // the status to COMPLETED then re-fetch highlights.
        dispatch(retrieveHighlights(note.id))
      }
    })

    console.log("Successfully triggered transaction!")
  }, [data?.hash])

  useEffect(() => {
    if (!error) return

    console.error("Error creating new collectible", error)

    track("error creating collectible", {
      error,
    })
  }, [error])

  return (
    <div className="flex justify-center flex-col w-full">
      <Button
        type="button"
        text={props.btnText || "Collect highlight"}
        disabled={!!error}
        onClick={async () => {
          buy()
        }}
      />

      {error && (
        <>
          <ErrorMessage error={error} />
          <SwitchChainButton chain={props.chain} />
        </>
      )}
    </div>
  )
}
